import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/BrandLogo";

const ABSTRACT_BG = "https://images.unsplash.com/photo-1593642634367-d91a135587b5?auto=format&fit=crop&w=1200&h=1600&q=85";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Bienvenue !");
            navigate("/app");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur de connexion");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/app";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden lg:block w-1/2 relative overflow-hidden" data-theme="dark">
                <img src={ABSTRACT_BG} className="absolute inset-0 w-full h-full object-cover" alt="" />
                {/* Brand-aligned dual gradient: deep cool top-left → warm orange bottom-right.
                   Gives the photo Ticketify's signature blue→green→warm energy and ensures
                   the wordmark and headline stay legible on top. */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/75 via-[#1D1D1F]/65 to-[#34C759]/55" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
                <Link to="/accueil" className="absolute top-8 left-8 flex items-center gap-2" data-testid="auth-back-home">
                    <BrandLogo height={44} />
                </Link>
                <div className="absolute bottom-12 left-12 right-12 text-white">
                    <p className="text-sm uppercase tracking-[0.2em] opacity-70 mb-2">Bienvenue</p>
                    <h2 className="text-4xl font-semibold tracking-tight max-w-sm">Vos tickets, simplifiés.</h2>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-sm tk-rise">
                    <div className="lg:hidden mb-12 flex items-center gap-2">
                        <BrandLogo height={48} />
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">Connexion</h1>
                    <p className="mt-2 text-[#86868B]">Heureux de vous revoir.</p>

                    <button
                        type="button"
                        onClick={handleGoogle}
                        data-testid="google-signin-btn"
                        className="mt-8 w-full flex items-center justify-center gap-3 bg-white border border-black/10 rounded-full py-3 font-medium hover:bg-[#FAFAFA] transition active:scale-[0.98]"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continuer avec Google
                    </button>

                    <p className="mt-2 text-[11px] text-[#86868B] text-center leading-relaxed">
                        Si vous avez déjà un compte avec le même email, votre compte Google sera lié automatiquement.
                    </p>

                    <div className="flex items-center gap-3 my-6 text-xs text-[#86868B] uppercase tracking-widest">
                        <div className="flex-1 h-px bg-black/10" />
                        ou
                        <div className="flex-1 h-px bg-black/10" />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="vous@exemple.com"
                                className="tk-input"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-[#1D1D1F]">Mot de passe</label>
                                <Link to="/forgot-password" className="text-xs text-[#007AFF] hover:underline" data-testid="login-forgot-link">
                                    Oublié ?
                                </Link>
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="••••••••"
                                className="tk-input"
                                data-testid="login-password-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="tk-btn tk-btn-primary w-full justify-center"
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Connexion…" : "Se connecter"}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-[#86868B]">
                        Pas encore de compte ?{" "}
                        <Link to="/register" className="text-[#007AFF] font-medium" data-testid="login-to-register">
                            Créer un compte
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
